import { useCallback, useMemo } from "react";
import type { TenantRead } from "@harmonyso/tenants-sdk";
import { useAllTenants } from "@/hooks/use-tenants";
import { CELLS, PRIMARY_CELL, findCell, type Cell } from "@/lib/cells/cells";
import { homeCellExclusions } from "@/lib/cells/cell-statistics";
import type { CellFailure } from "@/lib/cells/cell-tenants";
import { EXCLUDED_TENANT_IDS } from "@/lib/constants/excluded-tenants";

export interface CellScope {
  showCells: boolean;
  tenants: TenantRead[] | undefined;
  tenantsLoading: boolean;
  tenantFailures: CellFailure[];
  selectedTenant: TenantRead | undefined;
  /** The cells to read: the tenant's own cell, the chosen cell, or all. */
  queriedCells: Cell[];
  /** A tenant is selected but its cell can't be told: nothing is read. */
  tenantCellUnknown: boolean;
  /** A cell's tenants couldn't be listed, so they can't be left out elsewhere. */
  exclusionsIncomplete: boolean;
  /** The tenants to leave out of a cell's numbers: others' tenants and internal ones. */
  excludeTenantIds: (cell: Cell) => string[];
}

/**
 * Which cells an agent page reads, for a cell choice ("all" or a cell id)
 * and an optional selected tenant. With several cells, nothing is read until
 * the tenant lists are in, because they decide a tenant's cell and what each
 * cell's numbers leave out. A single-cell build reads the primary cell at once.
 */
export function useCellScope({
  cellFilter,
  tenantId,
}: {
  cellFilter: string;
  tenantId?: string;
}): CellScope {
  const showCells = CELLS.length > 1;
  const {
    data: tenants,
    isLoading: tenantsLoading,
    failures: tenantFailures,
  } = useAllTenants();
  const selectedTenant = tenantId
    ? tenants?.find((tenant) => tenant.id === tenantId)
    : undefined;

  const listsSettled = !tenantsLoading;
  const tenantCellUnknown =
    showCells &&
    !!tenantId &&
    !selectedTenant &&
    listsSettled &&
    tenantFailures.length > 0;
  const exclusionsIncomplete =
    showCells && !tenantId && listsSettled && tenantFailures.length > 0;

  const queriedCells = useMemo((): Cell[] => {
    if (!showCells) return [PRIMARY_CELL];
    if (tenantsLoading) return [];
    if (tenantId) {
      const tenantCell = findCell(selectedTenant?.deployment_cell);
      if (tenantCell) return [tenantCell];
      if (tenantCellUnknown) return [];
      // Every cell answered and none has the tenant.
      return [PRIMARY_CELL];
    }
    return cellFilter === "all"
      ? CELLS
      : [findCell(cellFilter) ?? PRIMARY_CELL];
  }, [
    showCells,
    tenantsLoading,
    tenantId,
    selectedTenant,
    tenantCellUnknown,
    cellFilter,
  ]);

  const excludeTenantIds = useCallback(
    (cell: Cell) =>
      showCells && !tenantId
        ? homeCellExclusions(cell.id, tenants ?? [], EXCLUDED_TENANT_IDS)
        : // A tenant picked on purpose is shown even when it's an internal one.
          EXCLUDED_TENANT_IDS.filter((id) => id !== tenantId),
    [showCells, tenantId, tenants],
  );

  return {
    showCells,
    tenants,
    tenantsLoading,
    tenantFailures,
    selectedTenant,
    queriedCells,
    tenantCellUnknown,
    exclusionsIncomplete,
    excludeTenantIds,
  };
}
